import { effectScope, watch, type EffectScope } from 'vue'
import { defineStore } from 'pinia'
import { HttpStatusCode } from 'axios'
import { API } from '../../../services'
import { useUserStore } from '../../../store/user'
import { getUserActiveInventoryLocationId } from '../utils/transferLocationContext'
import {
  emptyInventoryHealthSummary,
  parseInventoryHealthSummary,
  type InventoryHealthSummary,
} from '../inventoryHealthModel'

/** Ignore a stale summary response when business or warehouse context changes mid-request. */
let inventoryHealthRequest = 0

/** Detached scope so the context watch outlives any single page/component. */
let healthWatchScope: EffectScope | null = null

type InventoryHealthState = {
  loading: boolean
  /** i18n key when the last fetch failed; null when ok or cleared. */
  errorKey: string | null
  summary: InventoryHealthSummary
}

export const useInventoryHealthStore = defineStore('inventoryHealth', {
  state: (): InventoryHealthState => ({
    loading: false,
    errorKey: null,
    summary: emptyInventoryHealthSummary(),
  }),

  actions: {
    /**
     * Start a single shared watch for permission / business / warehouse changes.
     * Safe to call from every consumer; only the first call registers the watch.
     */
    bindContextWatch() {
      if (healthWatchScope) return

      const userStore = useUserStore()
      healthWatchScope = effectScope(true)
      healthWatchScope.run(() => {
        watch(
          () =>
            [
              userStore.hasPermission('read', 'item'),
              userStore.authUser?.active_business?.uuid ?? null,
              getUserActiveInventoryLocationId(userStore.authUser),
            ] as const,
          () => {
            void this.fetchSummary()
          },
          { immediate: true },
        )
      })
    },

    async fetchSummary() {
      const generation = ++inventoryHealthRequest
      const userStore = useUserStore()
      const canReadItems = userStore.hasPermission('read', 'item')
      const activeLocationId = getUserActiveInventoryLocationId(userStore.authUser)

      // Counts are scoped to the session's active warehouse and require item read access.
      if (!canReadItems || activeLocationId == null) {
        this.summary = emptyInventoryHealthSummary()
        this.errorKey = null
        this.loading = false
        return
      }

      this.loading = true
      this.errorKey = null

      try {
        const { status, data } = await API.items.getInventoryStatusSummary()
        if (generation !== inventoryHealthRequest) return

        if (status !== HttpStatusCode.Ok || !data?.data) {
          this.summary = emptyInventoryHealthSummary()
          this.errorKey = 'inventoryHealth.loadError'
          return
        }

        this.summary = parseInventoryHealthSummary(data.data)
      } catch {
        if (generation !== inventoryHealthRequest) return
        this.summary = emptyInventoryHealthSummary()
        this.errorKey = 'inventoryHealth.loadError'
      } finally {
        if (generation === inventoryHealthRequest) {
          this.loading = false
        }
      }
    },
  },
})
